import { useState, useEffect, useCallback } from 'react'

// Unteransichten (Chat, Unterhaltung, "Neue Nachricht", Passwort ändern …) stehen in der URL
// und bekommen je einen Eintrag im Browser-Verlauf. So geht Android-Zurück (Wischgeste)
// und der Zurück-Button im Browser genau einen Schritt zurück.
// history.state.depth zählt, wie viele Schritte innerhalb der Seite zurückgegangen werden kann.

const readParams = () => new URLSearchParams(window.location.search)
const currentDepth = () => window.history.state?.depth || 0

function urlFor(params) {
  const q = params.toString()
  return window.location.pathname + (q ? `?${q}` : '')
}

function apply(params, changes) {
  const next = new URLSearchParams(params)
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === undefined || value === false) next.delete(key)
    else next.set(key, String(value))
  }
  return next
}

/** true, wenn Zurück innerhalb der aktuellen Seite bleibt */
export function canGoBackInPage() {
  return currentDepth() > 0
}

/**
 * Push-Tap öffnet `/pastor?chat=<id>` bzw. `/admin?chat=<id>`. Damit Zurück dann
 * Unterhaltung → Chatliste → Dashboard geht, wird der Verlauf entsprechend aufgebaut.
 * Einmal vor dem ersten Rendern aufrufen.
 */
export function seedDeepLinks() {
  const params = readParams()
  const id = parseInt(params.get('chat'))
  if (isNaN(id)) return
  const path = window.location.pathname
  window.history.replaceState({ depth: 0 }, '', path)
  window.history.pushState({ depth: 1 }, '', `${path}?screen=chat`)
  window.history.pushState({ depth: 2 }, '', `${path}?screen=chat&c=${id}`)
}

/**
 * URL-Parameter der Seite + Navigation.
 *  push(changes)     – neuer Schritt (Zurück kehrt hierher zurück)
 *  replace(changes)  – aktuellen Schritt ersetzen
 *  back(fallback)    – einen Schritt zurück; ohne Verlauf stattdessen `fallback` anwenden
 * Nur einmal pro Seite verwenden und die Werte nach unten weiterreichen.
 */
export function useViewParams() {
  const [params, setParams] = useState(readParams)

  useEffect(() => {
    const onPop = () => setParams(readParams())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const push = useCallback((changes) => {
    const next = apply(readParams(), changes)
    window.history.pushState({ depth: currentDepth() + 1 }, '', urlFor(next))
    setParams(next)
  }, [])

  const replace = useCallback((changes) => {
    const next = apply(readParams(), changes)
    window.history.replaceState({ depth: currentDepth() }, '', urlFor(next))
    setParams(next)
  }, [])

  const back = useCallback((fallback = {}) => {
    if (currentDepth() > 0) {
      window.history.back() // popstate aktualisiert die Ansicht
    } else {
      const next = apply(readParams(), fallback)
      window.history.replaceState({ depth: 0 }, '', urlFor(next))
      setParams(next)
    }
  }, [])

  return { params, push, replace, back }
}
